// Angular
import { Injectable } from '@angular/core';
// Models
import { IUserLocale } from '@shared/models';
// RxJs & Lodash
import { toString } from 'lodash';

/**
 * CHANGE THIS KEY ON BREAKING CHANGES
 * Old keys will be automatically removed in constructor
 */
const PRIME_GLOBAL_SHARED_DATA_KEY = 'prime_globalshared_data_v2';

@Injectable({
    providedIn: 'root'
})
export class CommonService {

    public sharedData: any = {};

    constructor() {
        /** REMOVE OLD GLOBAL SHARED STORAGE KEYS */
        Object.keys(localStorage)
            .filter(key => key.startsWith('prime_globalshared_data_') && key !== PRIME_GLOBAL_SHARED_DATA_KEY)
            .forEach(key => localStorage.removeItem(key));
    }

    /**
     * SET SHARED OBJECT INSTANCE STOARGE
     * @param key: string
     * @param value: any
     * @returns void
     */
    setIntoSharedData(key: string, value: any): void {
        this.sharedData[key] = value;
    }

    /**
     * GET SHARED OBJECT INSTANCE FROM STOARGE
     * @param key: string
     * @returns any
     */
    getFromSharedData(key: string): any {
        return this.sharedData[key];
    }

    /**
     * SET LOCAL STOARGE SERVICES
     * @param key: string
     * @param value: any
     * @returns void
     */
    setIntoLocalStorage(key: string, value: any): void {
        const rawData = localStorage.getItem(PRIME_GLOBAL_SHARED_DATA_KEY);
        let globalSharedData: Record<string, any>;

        if (rawData && rawData !== null) {
            globalSharedData = JSON.parse(rawData);
            globalSharedData[key] = value;
        }
        else {
            globalSharedData = {};
            globalSharedData[key] = value;
        }

        localStorage.setItem(
            PRIME_GLOBAL_SHARED_DATA_KEY,
            JSON.stringify(globalSharedData)
        );
    }

    /**
     * REMOVE SHARED OBJECT INSTANCE FROM LOCAL STOARGE
     * @param key: string
     * @returns any
     */
    removeFromLocalStorage(key: string): any {
        const rawData = localStorage.getItem(PRIME_GLOBAL_SHARED_DATA_KEY);
        let globalSharedData: Record<string, any> | null = null;

        if (rawData && rawData !== null) {
            globalSharedData = JSON.parse(rawData);
            delete globalSharedData[key];
        }

        localStorage.setItem(
            PRIME_GLOBAL_SHARED_DATA_KEY,
            JSON.stringify(globalSharedData)
        );

        return globalSharedData;
    }

    /**
     * GET SHARED OBJECT INSTANCE FROM LOCAL STOARGE
     * @param key: string
     * @returns any
     */
    getFromLocalStorage(key: string): any {
        const rawData = localStorage.getItem(PRIME_GLOBAL_SHARED_DATA_KEY);
        let value;

        if (rawData && rawData !== null) {
            const globalSharedData: Record<string, any> = JSON.parse(rawData);
            value = globalSharedData[key];
        }

        return value;
    }

    /**
     * SET SESSION STORAGE SERVICES
     * @param sessionkey: any
     * @param inputSessionObj: any
     * @returns void
     */
    setInSessionStorage(sessionkey: any, inputSessionObj: any): void {
        try {
            const sessionObjString = sessionStorage.getItem(sessionkey);
            let sessionObj: any = {};
            if (sessionObjString != null) {
                sessionObj = JSON.parse(sessionObjString);
            }
            // eslint-disable-next-line guard-for-in
            for (const key in inputSessionObj) {
                sessionObj[key] = inputSessionObj[key];
            }
            sessionStorage.setItem(sessionkey, JSON.stringify(sessionObj));
        } catch (ex) {
            console.log('Exception in setInSessionStorage', ex);
        }
    }

    /**
     * GET FROM SESSION STORAGE SERVICES
     * @param key: any
     * @param subKey: any
     * @returns any
     */
    getFromSessionStorage(key: string, subKey?: string | number): any {
        try {
            let sessionObj: any = sessionStorage.getItem(key);
            let sessionValue = null;
            if (null != sessionObj) {
                sessionObj = JSON.parse(sessionObj);
                if (subKey) {
                    if (null != sessionObj[subKey]) {
                        sessionValue = sessionObj[subKey];
                    }
                } else {
                    sessionValue = sessionObj;
                }
            }
            return sessionValue;
        } catch (ex) {
            console.log('Exception in getFromSessionStorage', ex);
        }
    }

    /**
     * REMOVE FROM SESSION STORAGE SERVICES
     * @param key: any
     * @param subKey: any
     * @returns void
     */
    removeFromSessionStorage(key: string, subKey?: string | number): void {
        const sessionObj = sessionStorage.getItem(key);
        try {
            if (sessionObj !== null) {
                if (subKey) {
                    const sessionObject = JSON.parse(sessionObj);
                    if (sessionObject.hasOwnProperty(subKey)) {
                        delete sessionObject[subKey];
                        sessionStorage.setItem(key, JSON.stringify(sessionObject));
                    }
                } else {
                    sessionStorage.removeItem(key);
                }
            }
        } catch (ex) {
            console.log('Exception in removeFromSessionStorage', ex);
        }
    }

    /**
     * CLEAR ALL STOARGE , SESSION SERVICES
     * @returns void
     */
    clearAll(): void {
        localStorage.clear();
        sessionStorage.clear();
    }

    /**
     * Returns user locale
     * @param langId: string
     * @returns IUserLocale
     */
    getUserLocale(locale: string): IUserLocale {
        const _locale = toString(locale);
        switch (_locale) {
            case 'sv-se':
            case 'SE':
            case '752':
                return { id: '752', locale: 'sv-se', code: 'SE', name: 'sweden', flag: 'sweden', utf: 'sv_SE.UTF8', locale_code: 'sv_SE', currency: 'SEK', timezone: 'Europe/Stockholm' };

            case 'fi-fi':
            case 'FI':
            case '246':
                return { id: '246', locale: 'fi-fi', code: 'FI', name: 'finland', flag: 'finland', utf: 'fi_FI.UTF8', locale_code: 'fi_FI', currency: 'EUR', timezone: 'Europe/Helsinki' };

            case 'da-dk':
            case 'DK':
            case '208':
                return { id: '208', locale: 'da-dk', code: 'DK', name: 'denmark', flag: 'denmark', utf: 'da_DK.UTF8', locale_code: 'da_DK', currency: 'DKK', timezone: 'Europe/Stockholm' };

            case 'de-de':
            case 'DE':
            case '276':
                return { id: '276', locale: 'de-de', code: 'DE', name: 'germany', flag: 'germany', utf: 'de_DE.UTF8', locale_code: 'de_DE', currency: 'EUR', timezone: 'Europe/Stockholm' };

            case 'nb-no':
            case 'NO':
            case '578':
                return { id: '578', locale: 'nb-no', code: 'NO', name: 'norway', flag: 'norway', utf: 'no_NO.UTF8', locale_code: 'no_NO', currency: 'NOK', timezone: 'Europe/Stockholm' };

            case 'es-es':
            case 'ES':
            case '724':
                return { id: '724', locale: 'es-es', code: 'ES', name: 'spain', flag: 'spain', utf: 'es_ES.UTF8', locale_code: 'es_ES', currency: 'EUR', timezone: 'Europe/Stockholm' };

            case 'is-is':
            case 'IS':
            case '352':
                return { id: '352', locale: 'is-is', code: 'IS', name: 'iceland', flag: 'iceland', utf: 'is_IS.UTF8', locale_code: 'is_IS', currency: 'ISK', timezone: 'Atlantic/Reykjavik' };

            case 'fr-fr':
            case 'FR':
            case '250':
                return { id: '250', locale: 'fr-fr', code: 'FR', name: 'france', flag: 'france', utf: 'fr_FR.UTF8', locale_code: 'fr_FR', currency: 'EUR', timezone: 'Europe/Stockholm' };

            case 'it-it':
            case 'IT':
            case '380':
                return { id: '380', locale: 'it-it', code: 'IT', name: 'italy', flag: 'italy', utf: 'it_IT.UTF8', locale_code: 'it_IT', currency: 'EUR', timezone: 'Europe/Stockholm' };

            case 'pl-pl':
            case 'PL':
            case '616':
                return { id: '616', locale: 'pl-pl', code: 'PL', name: 'poland', flag: 'poland', utf: 'pl_PL.UTF8', locale_code: 'pl_PL', currency: 'PLN', timezone: 'Europe/Warsaw' };

            case 'en-se':
            case 'EN':
            case '840':
                return { id: '840', locale: 'en-se', code: 'EN', name: 'usa', flag: 'usa', utf: 'en_US.UTF8', locale_code: 'en_US', currency: 'USD', timezone: 'Europe/Stockholm' };

            default:
                return { id: '752', locale: 'sv-se', code: 'SE', name: 'sweden', flag: 'sweden', utf: 'sv_SE.UTF8', locale_code: 'sv_SE', currency: 'SEK', timezone: 'Europe/Stockholm' };
        }
    }


}
